/**
 * POS printer configuration — persisted locally (and Electron store on desktop).
 * Survives offline/online; only changes when the user presses Save in POS Settings.
 */

export const PRINTER_KEYS = {
  COUNTER_USB: 'pos_counter_printer_usb',
  COUNTER_IP: 'pos_counter_printer_ip',
  COUNTER_LEGACY: 'pos_counter_printer',
  KITCHEN_USB: 'pos_kitchen_printer_usb',
  KITCHEN_IP: 'pos_kitchen_printer_ip',
  KITCHEN_LEGACY: 'pos_kitchen_printer',
};

const ALL_PERSISTED_KEYS = [
  PRINTER_KEYS.COUNTER_USB,
  PRINTER_KEYS.COUNTER_IP,
  PRINTER_KEYS.COUNTER_LEGACY,
  PRINTER_KEYS.KITCHEN_USB,
  PRINTER_KEYS.KITCHEN_IP,
  PRINTER_KEYS.KITCHEN_LEGACY,
];

const isElectronStore = () =>
  typeof window !== 'undefined' &&
  !!window.electronAPI?.isElectron &&
  !!window.electronAPI?.store;

function readLocal(key) {
  try {
    return localStorage.getItem(key);
  } catch (_) {
    return null;
  }
}

function writeLocal(key, value) {
  try {
    localStorage.setItem(key, value ?? '');
  } catch (_) {
    /* ignore quota */
  }
}

/** Sync read — used by print paths at runtime. */
export function getSavedKitchenIp() {
  return (readLocal(PRINTER_KEYS.KITCHEN_IP) || '').trim();
}

export function getSavedCounterIp() {
  return (readLocal(PRINTER_KEYS.COUNTER_IP) || '').trim();
}

export function getSavedKitchenUsb() {
  return (
    readLocal(PRINTER_KEYS.KITCHEN_USB) ||
    readLocal(PRINTER_KEYS.KITCHEN_LEGACY) ||
    ''
  ).trim();
}

export function getSavedCounterUsb() {
  return (
    readLocal(PRINTER_KEYS.COUNTER_USB) ||
    readLocal(PRINTER_KEYS.COUNTER_LEGACY) ||
    ''
  ).trim();
}

/**
 * Copy Electron encrypted store → localStorage when local is empty (desktop cold start).
 */
export async function hydratePrinterSettingsFromElectronStore() {
  if (!isElectronStore()) return;

  await Promise.all(
    ALL_PERSISTED_KEYS.map(async (key) => {
      const local = readLocal(key);
      if (local != null && local !== '') return;

      try {
        const stored = await window.electronAPI.store.get(key);
        if (typeof stored === 'string' && stored.trim()) {
          writeLocal(key, stored.trim());
        }
      } catch (_) {
        /* ignore */
      }
    })
  );
}

async function persistKey(key, value) {
  const normalized = value == null ? '' : String(value).trim();
  writeLocal(key, normalized);

  if (isElectronStore()) {
    try {
      if (normalized) {
        await window.electronAPI.store.set(key, normalized);
      } else {
        await window.electronAPI.store.delete(key);
      }
    } catch (_) {
      /* non-blocking */
    }
  }

  return normalized;
}

/** Persist kitchen/counter IP — only call from POS Settings Save button. */
export async function saveKitchenPrinterIp(ip) {
  return persistKey(PRINTER_KEYS.KITCHEN_IP, ip);
}

export async function saveCounterPrinterIp(ip) {
  return persistKey(PRINTER_KEYS.COUNTER_IP, ip);
}

/** Persist USB printer name — only call from POS Settings Save button. */
export async function saveKitchenPrinterUsb(printerName) {
  const v = await persistKey(PRINTER_KEYS.KITCHEN_USB, printerName);
  await persistKey(PRINTER_KEYS.KITCHEN_LEGACY, printerName);
  return v;
}

export async function saveCounterPrinterUsb(printerName) {
  const v = await persistKey(PRINTER_KEYS.COUNTER_USB, printerName);
  await persistKey(PRINTER_KEYS.COUNTER_LEGACY, printerName);
  return v;
}

/**
 * Receipt language — independent of the app/system language. Empty string means
 * "follow the app language". Persisted locally so it survives offline/reloads.
 */
export const RECEIPT_LANGUAGE_KEY = 'pos_receipt_language';

export function getReceiptLanguage() {
  return (readLocal(RECEIPT_LANGUAGE_KEY) || '').trim();
}

export function setReceiptLanguage(lang) {
  writeLocal(RECEIPT_LANGUAGE_KEY, lang || '');
  if (isElectronStore()) {
    try {
      if (lang) window.electronAPI.store.set(RECEIPT_LANGUAGE_KEY, lang);
      else window.electronAPI.store.delete(RECEIPT_LANGUAGE_KEY);
    } catch (_) { /* non-blocking */ }
  }
}

export async function loadPrinterSettingsForUi() {
  await hydratePrinterSettingsFromElectronStore();
  return {
    kitchenIp: getSavedKitchenIp(),
    counterIp: getSavedCounterIp(),
    kitchenUsb: getSavedKitchenUsb(),
    counterUsb: getSavedCounterUsb(),
  };
}
